import type { SampleType } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged, PaginationParams } from "../_shared/pagination.types";
import { SAMPLE_TYPES_ENDPOINTS } from "./sample-types.consts";
import type { SampleTypeCreateRequest, SampleTypeUpdateRequest } from "./sample-types.types";

/**
 * Sample Types API Client
 *
 * Handles CRUD for sample type mappings under a single instrument.
 * The natural key for every targeted operation is `(sampleCode, mappingKey)`.
 */
export class SampleTypesClient extends BaseClient {
  /**
   * Fetch a page of sample types for an instrument.
   */
  async fetchSampleTypes(
    instrumentId: number,
    params?: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<SampleType>> {
    return this.get<Paged<SampleType>>(SAMPLE_TYPES_ENDPOINTS.SAMPLE_TYPES(instrumentId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Fetch a single sample type by its `(sampleCode, mappingKey)` pair.
   */
  async fetchSampleTypeByKeys(
    instrumentId: number,
    sampleCode: string,
    mappingKey: string,
    signal?: AbortSignal | null,
  ): Promise<SampleType> {
    return this.get<SampleType>(SAMPLE_TYPES_ENDPOINTS.SAMPLE_TYPE_BY_KEYS(instrumentId, sampleCode, mappingKey), {
      signal,
    });
  }

  /**
   * Create a sample type for an instrument.
   */
  async createSampleType(
    instrumentId: number,
    data: SampleTypeCreateRequest,
    signal?: AbortSignal | null,
  ): Promise<SampleType> {
    return this.post<SampleType>(SAMPLE_TYPES_ENDPOINTS.SAMPLE_TYPES(instrumentId), {
      body: data,
      signal,
    });
  }

  /**
   * Update a sample type. Only `name` is updatable per the API contract.
   * The `(sampleCode, mappingKey)` pair identifies the row in the URL.
   */
  async updateSampleType(
    instrumentId: number,
    sampleCode: string,
    mappingKey: string,
    data: SampleTypeUpdateRequest,
    signal?: AbortSignal | null,
  ): Promise<SampleType> {
    return this.put<SampleType>(SAMPLE_TYPES_ENDPOINTS.UPDATE_SAMPLE_TYPE(instrumentId, sampleCode, mappingKey), {
      body: data,
      signal,
    });
  }

  /**
   * Delete a sample type by its `(sampleCode, mappingKey)` pair.
   */
  async deleteSampleType(
    instrumentId: number,
    sampleCode: string,
    mappingKey: string,
    signal?: AbortSignal | null,
  ): Promise<void> {
    return this.delete<never>(SAMPLE_TYPES_ENDPOINTS.DELETE_SAMPLE_TYPE(instrumentId, sampleCode, mappingKey), {
      signal,
    });
  }
}
